import React, { useLayoutEffect, useRef, useState } from 'react'
import TextStylingComponent from '../TextStylingComponent/TextStylingComponent';
import FloatyStylingWrapper from './FloatyStylingWrapper';
import { pageHeight, pageWidth, canvasLeftShift } from '../../constants/gridConfig';
import { useStore } from '../../store/store';
import { useOverlay, leftShownStatus } from '../../contexts/OverlayContext';

const FloatyTextStyling = ({
  editorState,
  setEditorState,
  defaultFontSize,
  defaultFontFamily,
  defaultColor,
  defaultInlineStyles,
  boxId,
  updateBox,
  hasAlignement,
  boxAlignement,
  onAlignmentChange,
  textStyle,
  hasForm,
  hasList,
  hasFontSize = true,
  colors,
}) => {
  const [menuPosition, setMenuPosition] = useState({ top: null, left: null });
  // Position brute de la sélection (avant décalage vertical par la hauteur de
  // la barre), gardée à part de menuPosition pour pouvoir la recorriger une
  // fois la hauteur réelle connue (cf. effet ci-dessous).
  const anchorRef = useRef({ top: null, left: null })
  // Bornes de la sélection pour laquelle la barre a été placée.
  const placedSelectionRef = useRef(null)
  const landscape = useStore(({ landscape }) => landscape)
  const ref = useRef()
  const { overlayProps } = useOverlay();

  const shownStatus = overlayProps.left.shownStatus

  useLayoutEffect(() => {
    const selection = window.getSelection && window.getSelection();
    const editorSelection = editorState.getSelection();

    // Vérifier si la sélection est valide et non vide
    const hasValidSelection = selection && selection.rangeCount > 0 &&
      (editorSelection.getStartOffset() !== editorSelection.getEndOffset() ||
        editorSelection.getStartKey() !== editorSelection.getEndKey());

    // Même sélection, seul son style a changé (taille, police… appliquées
    // depuis la barre) : la barre reste en place. Sans ça, elle suivait le bord
    // gauche du texte sélectionné, qui bouge à chaque changement de taille
    // quand le texte est centré.
    const bounds = [
      editorSelection.getAnchorKey(),
      editorSelection.getAnchorOffset(),
      editorSelection.getFocusKey(),
      editorSelection.getFocusOffset(),
    ].join(':')
    if (hasValidSelection && anchorRef.current.top !== null && placedSelectionRef.current === bounds) return
    placedSelectionRef.current = bounds

    if (hasValidSelection) {
      const selectionRange = selection.getRangeAt(0);
      const { top, left } = selectionRange.getBoundingClientRect()
      if (top !== 0 && left !== 0) {
        anchorRef.current = { top, left };
        // Hauteur réelle de la barre inconnue tant qu'elle n'est pas montée
        // (1 ou 2 lignes selon les contrôles actifs) : estimation ici,
        // corrigée par l'effet ci-dessous dès que ref.current existe.
        const estimatedHeight = ref.current?.clientHeight ?? 50
        setMenuPosition({ top: top - estimatedHeight - 8, left })
      } else {
        anchorRef.current = { top: null, left: null };
        setMenuPosition({ top: null, left: null })
      }
    } else {
      anchorRef.current = { top: null, left: null };
      setMenuPosition({ top: null, left: null })
    }
  }, [editorState]);

  useLayoutEffect(() => {
    if (menuPosition.top === null || menuPosition.left === null) return
    if (!ref.current || anchorRef.current.top === null) return
    // Document plus centré sur l'écran (cf. canvasLeftShift, décalé vers
    // la droite pour compenser le panel de gauche plus large) : la borne
    // droite ("bord de page" pour ne pas empiéter sur le panel droit) doit
    // suivre ce même décalage. Pas en vue "variables" (formulaire plein
    // écran, document masqué) : borne = largeur totale de l'écran.
    const boundary = shownStatus !== leftShownStatus.VARIABLES
      ? (window.innerWidth / 2 + canvasLeftShift / 2 + (landscape ? pageHeight : pageWidth) / 2)
      : window.innerWidth
    const correctedTop = anchorRef.current.top - ref.current.clientHeight - 8
    let correctedLeft = anchorRef.current.left
    if (correctedLeft + ref.current.clientWidth > boundary) {
      correctedLeft = boundary - ref.current.clientWidth
    }
    if (Math.abs(correctedTop - menuPosition.top) > 0.5 || Math.abs(correctedLeft - menuPosition.left) > 0.5) {
      setMenuPosition({ top: correctedTop, left: correctedLeft })
    }
  }, [shownStatus, menuPosition, landscape])

  if (menuPosition.top === null || menuPosition.left === null) return null

  return (
    // z-index au-dessus de la barre flottante du container (verrou/copier/
    // supprimer, même FloatyStylingWrapper, z-index 9998) : les deux peuvent
    // se chevaucher visuellement quand une box est sélectionnée dans un
    // container lui-même sélectionné, la barre de texte doit rester au-dessus.
    <FloatyStylingWrapper menuPosition={menuPosition} zIndex={9999} ref={ref}>
      <TextStylingComponent
        editorState={editorState}
        setEditorState={setEditorState}
        defaultFontSize={defaultFontSize}
        defaultFontFamily={defaultFontFamily}
        defaultColor={defaultColor}
        defaultInlineStyles={defaultInlineStyles}
        updateBoxOnStylingChange={() => null}
        boxId={boxId}
        updateBox={updateBox}
        hasAlignement={hasAlignement}
        boxAlignement={boxAlignement}
        onAlignmentChange={onAlignmentChange}
        hasForm={hasForm}
        textStyle={textStyle}
        hasList={hasList}
        hasFontSize={hasFontSize}
        colors={colors}
      />
    </FloatyStylingWrapper>
  )
}

export default FloatyTextStyling
